import React, { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import type { AppDispatch } from '../../app/store';
import { AgSpinner } from '../ag-ui';
import { useShellMode } from '../../hooks/useShellMode';
import { fetchMyProfile } from '../../features/userProfile/userProfileSlice';
import {
  selectConsentementRequis, selectInvitationRequise, selectProfilEnAttente, selectStatutProfil,
} from '../../features/userProfile/selectors';
import { LOADING_STATES } from '../../constants';
import UpdateBanner from '../../pwa/UpdateBanner';
import { FACADE } from '../../facade/resolveFacade';

const Paperbase = lazy(() => import('../layout/Paperbase'));
const MobileShell = lazy(() => import('./MobileShell'));
const InvitationPage = lazy(() => import('../invitation/InvitationPage'));
const ConsentementPage = lazy(() => import('../consentement/ConsentementPage'));
// La façade Azaria (C26 SP3) : un seul composant, qui choisit lui-même bureau ou téléphone.
const AzariaShell = lazy(() => import('../../facade/azaria/AzariaShell'));

const Fallback: React.FC = () => (
  <div className="flex justify-center items-center h-screen" style={{ backgroundColor: 'var(--ag-background)' }}>
    <AgSpinner size="lg" />
  </div>
);

/**
 * Aiguillage shell mobile / bureau (C11). Les deux shells sont chargés
 * paresseusement : le téléphone ne télécharge ni Lexical ni PDF.js.
 *
 * Porte d'invitation (13/10/2026) : quand `GET /api/users/me` a répondu
 * 403 `INVITATION_REQUISE` (identité connectée sans profil, inscription sur
 * invitation), la page d'invitation remplace le shell, quel que soit l'écran.
 *
 * Porte de consentement (C27 SP2) : c'est l'aiguillage qui demande le profil
 * (une fois, quand la tranche est au repos) et attend sa réponse avant de
 * peindre un shell ; un profil sans la version courante des textes juridiques
 * voit la page de consentement à la place du shell. Les shells redemandent le
 * profil à leur façon (menu du compte, chargeur mobile) : la tranche n'étant
 * plus au repos, ils n'ajoutent pas d'appel. Une panne du profil (hors
 * invitation) ne bloque pas : le shell se monte comme avant.
 *
 * Façade par nom d'hôte (C26 SP3, cadrage § 9) : après les deux portes, un hôte
 * de `REACT_APP_AZARIA_HOSTS` (ou `?facade=azaria` en local) monte `AzariaShell`
 * à la place du couple MobileShell / Paperbase ; la façade est lue une fois au
 * démarrage (`FACADE`), jamais réévaluée.
 */
const ShellSwitch: React.FC = () => {
  const mode = useShellMode();
  const { instance } = useMsal();
  const dispatch = useDispatch<AppDispatch>();
  const statut = useSelector(selectStatutProfil);
  const invitationRequise = useSelector(selectInvitationRequise);
  const consentementRequis = useSelector(selectConsentementRequis);
  const profilEnAttente = useSelector(selectProfilEnAttente);

  useEffect(() => {
    if (statut === undefined || statut === LOADING_STATES.IDLE) {
      void dispatch(fetchMyProfile(instance));
    }
  }, [statut, dispatch, instance]);

  if (invitationRequise) {
    return (
      <Suspense fallback={<Fallback />}>
        <InvitationPage />
      </Suspense>
    );
  }
  if (profilEnAttente) {
    return <Fallback />;
  }
  if (consentementRequis) {
    return (
      <Suspense fallback={<Fallback />}>
        <ConsentementPage />
      </Suspense>
    );
  }
  if (FACADE.facade === 'azaria') {
    return (
      <>
        <Suspense fallback={<Fallback />}>
          <AzariaShell />
        </Suspense>
        <UpdateBanner raised={mode === 'mobile'} />
      </>
    );
  }
  return (
    <>
      <Suspense fallback={<Fallback />}>
        {mode === 'mobile' ? <MobileShell /> : <Paperbase />}
      </Suspense>
      {/* La bannière vit ici (dans les providers) pour connaître le shell actif :
          en mobile elle se pose au-dessus de la barre d'onglets. */}
      <UpdateBanner raised={mode === 'mobile'} />
    </>
  );
};

export default ShellSwitch;
